import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import VerifiedBadge from "@/components/VerifiedBadge";
import ScholarAvatar from "@/components/ScholarAvatar";
import {
  Users, BadgeCheck, Sparkles, ScrollText, CalendarDays, Briefcase,
  MessageSquare, TrendingUp, Search, GraduationCap, Trash2, AlertTriangle,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, hint, testid, accent = "emerald" }) {
  const accents = {
    emerald: "text-[color:var(--ayur-emerald)] bg-[color:var(--ayur-badge)]",
    gold: "text-[#8a6a2c] bg-[rgba(212,163,89,0.18)]",
    terracotta: "text-[color:var(--ayur-terracotta)] bg-[rgba(198,111,73,0.15)]",
    sage: "text-[color:var(--ayur-emerald)] bg-[color:var(--ayur-cream-2)]",
  };
  return (
    <div data-testid={testid} className="paper-card p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-2xl inline-flex items-center justify-center shrink-0 ${accents[accent]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-[color:var(--ayur-mute)]">{label}</div>
        <div className="font-serif-display text-3xl text-[color:var(--ayur-emerald)] leading-tight">{value}</div>
        {hint && <div className="text-xs text-[color:var(--ayur-mute)] mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purging, setPurging] = useState(false);

  const refreshStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const refreshUsers = async (query) => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/admin/users", { params: query ? { q: query } : {} });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingUsers(false);
    }
  };

  const openPurge = async () => {
    setPurgeOpen(true);
    setPurgePreview(null);
    try {
      const { data } = await api.get("/admin/purge-test-users/preview");
      setPurgePreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirmPurge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-test-users");
      toast.success(`Deleted ${data.deleted.users} test users and all their content`);
      setPurgeOpen(false);
      await Promise.all([refreshStats(), refreshUsers(q)]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPurging(false);
    }
  };

  const toggleGuru = async (u, promote) => {
    const path = promote
      ? `/admin/users/${u.id}/promote-guru`
      : `/admin/users/${u.id}/demote-guru`;
    try {
      await api.post(path);
      toast.success(promote
        ? `${u.full_name} is now a Guru`
        : `Guru status revoked for ${u.full_name}`);
      await Promise.all([refreshStats(), refreshUsers(q)]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/app");
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    refreshStats();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => refreshUsers(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  if (!stats) {
    return (
      <div className="text-center py-16 font-serif-display text-2xl text-[color:var(--ayur-emerald)]">
        Loading admin dashboard…
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="max-w-6xl mx-auto space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-4xl text-[color:var(--ayur-emerald)]">Admin dashboard</h1>
          <p className="text-[color:var(--ayur-mute)] mt-1">
            Community health, growth and moderation for VaidGram.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="open-purge-dialog"
            onClick={openPurge}
            variant="outline"
            className="border-[color:var(--ayur-terracotta)] text-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)] hover:text-white"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Purge test users
          </Button>
          <span className="pill bg-[color:var(--ayur-badge)] text-[color:var(--ayur-emerald)]">
            Signed in as {user?.full_name || "Admin"}
          </span>
        </div>
      </header>

      {/* Top-line stats */}
      <section>
        <h2 className="font-serif-display text-2xl text-[color:var(--ayur-emerald)] mb-3">Community</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            testid="stat-total-scholars"
            icon={Users}
            label="Total scholars joined"
            value={stats.total_scholars.toLocaleString()}
            hint="BAMS / MD / MS members (excludes gurus & admin)"
          />
          <StatCard
            testid="stat-new-week"
            icon={TrendingUp}
            label="New this week"
            value={stats.new_this_week.toLocaleString()}
            hint={`${stats.new_this_month.toLocaleString()} in last 30 days`}
            accent="terracotta"
          />
          <StatCard
            testid="stat-verified"
            icon={BadgeCheck}
            label="Verified scholars"
            value={stats.verified_scholars.toLocaleString()}
            hint={`${
              stats.total_scholars
                ? Math.round((stats.verified_scholars / stats.total_scholars) * 100)
                : 0
            }% of members`}
            accent="gold"
          />
          <StatCard
            testid="stat-gurus"
            icon={Sparkles}
            label="Curated gurus"
            value={stats.total_gurus.toLocaleString()}
            accent="sage"
          />
        </div>
      </section>

      {/* Activity stats */}
      <section>
        <h2 className="font-serif-display text-2xl text-[color:var(--ayur-emerald)] mb-3">Activity</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            testid="stat-posts"
            icon={ScrollText}
            label="Posts"
            value={stats.total_posts.toLocaleString()}
            hint={`${stats.posts_this_week.toLocaleString()} this week`}
          />
          <StatCard
            testid="stat-meetups"
            icon={CalendarDays}
            label="Meetups"
            value={stats.total_meetups.toLocaleString()}
            accent="terracotta"
          />
          <StatCard
            testid="stat-jobs"
            icon={Briefcase}
            label="Jobs posted"
            value={stats.total_jobs.toLocaleString()}
            hint={`${stats.sponsored_jobs} sponsored`}
            accent="gold"
          />
          <StatCard
            testid="stat-messages"
            icon={MessageSquare}
            label="Messages"
            value={stats.total_messages.toLocaleString()}
            hint={`${stats.total_conversations.toLocaleString()} conversations`}
            accent="sage"
          />
        </div>
      </section>

      {/* Top colleges + batches */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="paper-card p-6" data-testid="admin-top-colleges">
          <h3 className="font-serif-display text-xl text-[color:var(--ayur-emerald)] mb-3 inline-flex items-center gap-2">
            <GraduationCap className="w-5 h-5" /> Top colleges
          </h3>
          {stats.top_colleges.length === 0 ? (
            <div className="text-sm text-[color:var(--ayur-mute)]">No data yet.</div>
          ) : (
            <ul className="space-y-2">
              {stats.top_colleges.map((c) => (
                <li key={c.name} className="flex items-center justify-between text-sm">
                  <span className="truncate pr-3 text-[color:var(--ayur-ink)]">{c.name}</span>
                  <span className="pill">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="paper-card p-6" data-testid="admin-top-batches">
          <h3 className="font-serif-display text-xl text-[color:var(--ayur-emerald)] mb-3 inline-flex items-center gap-2">
            <CalendarDays className="w-5 h-5" /> Top batches
          </h3>
          {stats.top_batches.length === 0 ? (
            <div className="text-sm text-[color:var(--ayur-mute)]">No data yet.</div>
          ) : (
            <ul className="space-y-2">
              {stats.top_batches.map((b) => (
                <li key={b.year} className="flex items-center justify-between text-sm">
                  <span className="text-[color:var(--ayur-ink)]">Batch {b.year}</span>
                  <span className="pill">{b.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* User directory */}
      <section>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <h2 className="font-serif-display text-2xl text-[color:var(--ayur-emerald)]">All scholars</h2>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ayur-mute)]" />
            <Input
              data-testid="admin-user-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email or college"
              className="pl-9 bg-white w-72"
            />
          </div>
        </div>
        <div className="paper-card overflow-hidden">
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--ayur-cream-2)] text-[color:var(--ayur-mute)] uppercase tracking-wide text-xs sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3">Scholar</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">College</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Batch</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Role</th>
                  <th className="text-left px-4 py-3">Joined</th>
                  <th className="text-right px-4 py-3">Guru</th>
                </tr>
              </thead>
              <tbody data-testid="admin-user-table-body">
                {loadingUsers && (
                  <tr><td colSpan={6} className="text-center py-6 text-[color:var(--ayur-mute)]">Loading…</td></tr>
                )}
                {!loadingUsers && users.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-[color:var(--ayur-mute)]">No scholars match.</td></tr>
                )}
                {!loadingUsers && users.map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`admin-user-row-${u.id}`}
                    onClick={() => navigate(`/app/scholars/${u.id}`)}
                    className="border-t border-[color:var(--ayur-border)] hover:bg-white/70 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <ScholarAvatar user={u} size={36} className="shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-[color:var(--ayur-ink)] inline-flex items-center gap-1.5">
                            <span className="truncate max-w-[180px]">{u.full_name}</span>
                            {u.verified && <VerifiedBadge size={12} />}
                          </div>
                          <div className="text-xs text-[color:var(--ayur-mute)] truncate max-w-[220px]">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-[color:var(--ayur-ink-2)] truncate max-w-[220px]">
                      {u.college}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[color:var(--ayur-ink-2)]">{u.batch_year}</td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="pill">{u.is_guru ? "Guru" : u.role || "member"}</span>
                    </td>
                    <td className="px-4 py-3 text-[color:var(--ayur-mute)] whitespace-nowrap">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {u.is_guru ? (
                        <Button
                          data-testid={`admin-demote-guru-${u.id}`}
                          size="sm" variant="outline"
                          onClick={() => toggleGuru(u, false)}
                          className="border-[color:var(--ayur-border)] text-[color:var(--ayur-mute)] hover:bg-[color:var(--ayur-cream-2)]"
                        >
                          Revoke
                        </Button>
                      ) : (
                        <Button
                          data-testid={`admin-promote-guru-${u.id}`}
                          size="sm"
                          onClick={() => toggleGuru(u, true)}
                          className="bg-[color:var(--ayur-gold)] hover:bg-[color:var(--ayur-gold)]/90 text-white"
                        >
                          Make guru
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <DialogContent data-testid="purge-dialog" className="bg-[color:var(--ayur-cream)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[color:var(--ayur-terracotta)] inline-flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Purge test users
            </DialogTitle>
            <DialogDescription className="text-[color:var(--ayur-mute)]">
              This permanently deletes every seeded / QA test scholar and all their content.
              Admin and curated gurus are always preserved.
            </DialogDescription>
          </DialogHeader>

          {purgePreview === null ? (
            <div className="py-6 text-center text-[color:var(--ayur-mute)]">Counting…</div>
          ) : purgePreview.users === 0 ? (
            <div data-testid="purge-nothing" className="py-6 text-center text-[color:var(--ayur-emerald)] font-medium">
              Nothing to purge — no matching test users found.
            </div>
          ) : (
            <div data-testid="purge-preview" className="space-y-2 text-sm text-[color:var(--ayur-ink-2)]">
              <div className="flex justify-between border-b border-[color:var(--ayur-border)] pb-1.5"><span>Test users</span><b className="text-[color:var(--ayur-terracotta)]">{purgePreview.users}</b></div>
              <div className="flex justify-between"><span>Their posts</span><span>{purgePreview.posts}</span></div>
              <div className="flex justify-between"><span>Their comments</span><span>{purgePreview.comments}</span></div>
              <div className="flex justify-between"><span>Their meetups</span><span>{purgePreview.events}</span></div>
              <div className="flex justify-between"><span>Conversations</span><span>{purgePreview.conversations}</span></div>
              <div className="flex justify-between"><span>Messages</span><span>{purgePreview.messages}</span></div>
              <div className="flex justify-between"><span>Notifications</span><span>{purgePreview.notifications}</span></div>
              <div className="flex justify-between"><span>Uploaded files</span><span>{purgePreview.files}</span></div>
              <div className="flex justify-between"><span>Push subs</span><span>{purgePreview.push_subscriptions}</span></div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              data-testid="purge-cancel"
              variant="outline"
              onClick={() => setPurgeOpen(false)}
              className="border-[color:var(--ayur-border)]"
            >
              Cancel
            </Button>
            <Button
              data-testid="purge-confirm"
              onClick={confirmPurge}
              disabled={purging || !purgePreview || purgePreview.users === 0}
              className="bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {purging ? "Deleting…" : "Yes, delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
